/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

/**
 * Rapture state key for the edition identifier published by the backend.
 * See StatusStateContributor (self-hosted) and EditionStateContributor (cloud).
 */
export const EDITION_ID_STATE_KEY = 'editionId';

// Edition identifiers — match NexusEdition.getId() on the backend
export const EDITION_CORE = 'nexus-core-edition';
export const EDITION_COMMUNITY = 'nexus-community-edition';
export const EDITION_PRO = 'nexus-professional-edition';
export const EDITION_PRO_PLUS = 'nexus-professional-plus-edition';
export const EDITION_ENTERPRISE = 'nexus-enterprise-edition';
/** Cloud base edition — resolves to the same display name as Pro. */
export const EDITION_CLOUD = 'nexus-cloud-edition';

// KEEP IN SYNC with the DISPLAY_NAMES table in NX.State.getEditionDisplayName
// (nexus-rapture/.../NX/State.js). ExtJS cannot import from this module, so both
// surfaces must be updated together when a new edition identifier is added.
const EDITION_DISPLAY_NAMES: Record<string, string> = {
  [EDITION_CORE]: 'Core',
  [EDITION_COMMUNITY]: 'Community Edition',
  [EDITION_PRO]: 'Pro',
  [EDITION_PRO_PLUS]: 'Pro+',
  [EDITION_ENTERPRISE]: 'Enterprise',
  [EDITION_CLOUD]: 'Pro',
};

/** Default display name used before the backend has published an edition identifier. */
export const DEFAULT_EDITION_DISPLAY_NAME = 'Core';

/**
 * Maps an edition identifier (from Rapture state key {@code editionId}) to its
 * human-readable display name. Returns the {@link DEFAULT_EDITION_DISPLAY_NAME}
 * during the bootstrap window before state resolves so aria-labels and alt text
 * remain well-formed, and falls back to the raw identifier for unknown future
 * editions rather than silently hiding.
 */
export function getEditionDisplayName(editionId: string | undefined | null): string {
  if (!editionId) return DEFAULT_EDITION_DISPLAY_NAME;
  return EDITION_DISPLAY_NAMES[editionId] ?? editionId;
}

// Logo assets — consolidated here so both self-hosted and cloud reference one copy
// Pro+ and Enterprise use the Pro logo as a stopgap until design provides new assets
import headerWordmarkLogo from '../art/logos/logo-header-wordmark.svg';
import headerWordmarkDarkLogo from '../art/logos/logo-header-wordmark-dark.svg';
import coreLogo from '../art/logos/logo-core-edition-header.svg';
import coreDarkLogo from '../art/logos/logo-core-edition-header-dark.svg';
import ceLogo from '../art/logos/logo-community-edition-header.svg';
import ceDarkLogo from '../art/logos/logo-community-edition-header-dark.svg';
import proLogo from '../art/logos/logo-pro-edition-header.svg';
import proDarkLogo from '../art/logos/logo-pro-edition-header-dark.svg';
import proPlusLogo from '../art/logos/logo-pro-plus-edition-header.svg';
import proPlusDarkLogo from '../art/logos/logo-pro-plus-edition-header-dark.svg';
import enterpriseLogo from '../art/logos/logo-enterprise-edition-header.svg';
import enterpriseDarkLogo from '../art/logos/logo-enterprise-edition-header-dark.svg';

/**
 * Returns the common "sonatype nexus repository" wordmark for use in the global
 * header. The wordmark contains no edition text — the edition is shown separately
 * as a badge next to the logo.
 */
export function getHeaderWordmarkLogo(isDark: boolean): string {
  return isDark ? headerWordmarkDarkLogo : headerWordmarkLogo;
}

/**
 * Returns the full edition-branded logo asset URL for a given edition identifier
 * and theme, for surfaces that embed the edition name directly in the graphic.
 * The global header and login surfaces instead use {@link getHeaderWordmarkLogo}
 * combined with an edition badge.
 */
export function getEditionLogo(editionId: string | undefined | null, isDark: boolean): string {
  switch (editionId) {
    case EDITION_COMMUNITY:
      return isDark ? ceDarkLogo : ceLogo;
    case EDITION_PRO:
    case EDITION_CLOUD:
      return isDark ? proDarkLogo : proLogo;
    case EDITION_PRO_PLUS:
      return isDark ? proPlusDarkLogo : proPlusLogo;
    case EDITION_ENTERPRISE:
      return isDark ? enterpriseDarkLogo : enterpriseLogo;
    default:
      return isDark ? coreDarkLogo : coreLogo;
  }
}
